import { Vector3Tuple } from 'three';

export const euclideanDistance = (from: Vector3Tuple, to: Vector3Tuple): number => {
  const dx = from[0] - to[0];
  const dy = from[1] - to[1];
  const dz = from[2] - to[2];
  return Math.sqrt(dx ** 2 + dy ** 2 + dz ** 2);
};

export const euclideanDistanceAlongPath = (path: Vector3Tuple[]): number =>
  path.reduce((acc, cur, i) => (i === 0 ? 0 : acc + euclideanDistance(path[i - 1], cur)), 0);

export const pointBetween = (
  from: Vector3Tuple,
  to: Vector3Tuple,
  portion: number,
): Vector3Tuple => {
  const dx = to[0] - from[0];
  const dy = to[1] - from[1];
  const dz = to[2] - from[2];
  return [from[0] + dx * portion, from[1] + dy * portion, from[2] + dz * portion];
};

export const distancePointToSegment = (
  p: Vector3Tuple,
  a: Vector3Tuple,
  b: Vector3Tuple,
): { distance: number; point: Vector3Tuple } => {
  const abx = b[0] - a[0];
  const aby = b[1] - a[1];
  const abz = b[2] - a[2];

  const apx = p[0] - a[0];
  const apy = p[1] - a[1];
  const apz = p[2] - a[2];

  const abLenSq = abx * abx + aby * aby + abz * abz;

  if (abLenSq === 0) {
    return {
      distance: Math.sqrt(apx * apx + apy * apy + apz * apz),
      point: structuredClone(a),
    };
  }

  let t = (apx * abx + apy * aby + apz * abz) / abLenSq;
  t = Math.max(0, Math.min(1, t));

  const closestX = a[0] + abx * t;
  const closestY = a[1] + aby * t;
  const closestZ = a[2] + abz * t;

  const dx = p[0] - closestX;
  const dy = p[1] - closestY;
  const dz = p[2] - closestZ;

  return {
    distance: Math.sqrt(dx * dx + dy * dy + dz * dz),
    point: [closestX, closestY, closestZ],
  };
};

export const pointsAreEqual = (a: Vector3Tuple, b: Vector3Tuple): boolean =>
  a[0] === b[0] && a[1] === b[1] && a[2] === b[2];

export const pointIsBetweenTwoOthers = (
  pToCheck: Vector3Tuple,
  pA: Vector3Tuple,
  pB: Vector3Tuple,
): boolean => {
  const ax = pA[0],
    ay = pA[1],
    az = pA[2];
  const bx = pB[0],
    by = pB[1],
    bz = pB[2];
  const cx = pToCheck[0],
    cy = pToCheck[1],
    cz = pToCheck[2];

  if ((cx === ax && cy === ay && cz === az) || (cx === bx && cy === by && cz === bz)) {
    return true;
  }

  const abx = bx - ax,
    aby = by - ay,
    abz = bz - az;
  const acx = cx - ax,
    acy = cy - ay,
    acz = cz - az;
  const bcx = cx - bx,
    bcy = cy - by,
    bcz = cz - bz;

  const distAB = Math.sqrt(abx * abx + aby * aby + abz * abz);
  const distAC = Math.sqrt(acx * acx + acy * acy + acz * acz);
  const distBC = Math.sqrt(bcx * bcx + bcy * bcy + bcz * bcz);

  if (distAC > distAB || distBC > distAB) return false;

  const dotAC = abx * acx + aby * acy + abz * acz;
  const lenAB = distAB;
  const lenAC = distAC;

  const bax = -abx,
    bay = -aby,
    baz = -abz;
  const dotBC = bax * bcx + bay * bcy + baz * bcz;
  const lenBC = distBC;

  const productOfNormsAC = lenAB * lenAC;
  const productOfNormsBC = lenAB * lenBC;
  if (productOfNormsAC === 0 || productOfNormsBC === 0) return false;

  const cosAC = dotAC / productOfNormsAC;
  const cosBC = dotBC / productOfNormsBC;

  const angleAC = Math.acos(Math.max(-1, Math.min(1, cosAC)));
  const angleBC = Math.acos(Math.max(-1, Math.min(1, cosBC)));

  const epsilon = 0.0017;
  return Math.abs(angleAC) <= epsilon && Math.abs(angleBC) <= epsilon;
};

export const angleFromPoints = (a: Vector3Tuple, b: Vector3Tuple, c: Vector3Tuple): number => {
  const abx = a[0] - b[0];
  const aby = a[1] - b[1];
  const abz = a[2] - b[2];

  const bcx = b[0] - c[0];
  const bcy = b[1] - c[1];
  const bcz = b[2] - c[2];

  const dot = abx * bcx + aby * bcy + abz * bcz;

  const magAB = Math.sqrt(abx * abx + aby * aby + abz * abz);
  const magBC = Math.sqrt(bcx * bcx + bcy * bcy + bcz * bcz);

  const cosTheta = Math.min(1, Math.max(-1, dot / (magAB * magBC)));

  return Math.abs(Math.acos(cosTheta));
};

export const joinWithReasonableAngles = (
  a: Vector3Tuple,
  b: Vector3Tuple,
  c: Vector3Tuple,
):
  | [Vector3Tuple, Vector3Tuple, Vector3Tuple]
  | [Vector3Tuple, Vector3Tuple, Vector3Tuple, Vector3Tuple] => {
  const angle = angleFromPoints(a, b, c);
  if (angle < (Math.PI * 2) / 3) {
    return [a, b, c];
  }

  let abx = a[0] - b[0];
  let aby = a[1] - b[1];
  let abz = a[2] - b[2];

  const abMag = Math.sqrt(abx * abx + aby * aby + abz * abz);
  const abDist = abMag > 0 ? Math.min(2, abMag) : 0;
  abx = abMag > 0 ? (abx / abMag) * abDist : 0;
  aby = abMag > 0 ? (aby / abMag) * abDist : 0;
  abz = abMag > 0 ? (abz / abMag) * abDist : 0;

  const ab: Vector3Tuple = [b[0] + abx, b[1] + aby, b[2] + abz];

  let bcx = c[0] - b[0];
  let bcy = c[1] - b[1];
  let bcz = c[2] - b[2];

  const bcMag = Math.sqrt(bcx * bcx + bcy * bcy + bcz * bcz);
  const bcDist = bcMag > 0 ? Math.min(2, bcMag) : 0;
  bcx = bcMag > 0 ? (bcx / bcMag) * bcDist : 0;
  bcy = bcMag > 0 ? (bcy / bcMag) * bcDist : 0;
  bcz = bcMag > 0 ? (bcz / bcMag) * bcDist : 0;

  const bc: Vector3Tuple = [b[0] + bcx, b[1] + bcy, b[2] + bcz];

  return [a, ab, bc, c];
};

export const adjoiningIndexesOf = (
  a: Vector3Tuple[],
  b: Vector3Tuple[],
): [0 | -1, 0 | -1] | never[] => {
  if (!a.length || !b.length) {
    return [];
  }
  if (pointsAreEqual(a[0], b[0])) {
    return [0, 0];
  }
  if (pointsAreEqual(a[0], b[b.length - 1])) {
    return [0, -1];
  }
  if (pointsAreEqual(a[a.length - 1], b[0])) {
    return [-1, 0];
  }
  if (pointsAreEqual(a[a.length - 1], b[b.length - 1])) {
    return [-1, -1];
  }
  return [];
};
